import React from 'react';
import { X, Edit2, Calendar, Clock, User, FileText, Paperclip, Download } from 'lucide-react';
import api from '../../../api/config';

const downloadDoc = async (documentId) => {
  try {
    const response = await api.get(`/employee-documents/${documentId}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'documento.pdf');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch {
    alert('Error al descargar el documento');
  }
};
import EventCategoryIcon, { getCategoryLabel } from './components/EventCategoryIcon';
import EventTypeBadge from './components/EventTypeBadge';

/**
 * Modal de detalles completos de un evento de historial
 */
export default function HistoryEventDetails({ event, onClose, onEdit }) {
  const formatValue = (key, value) => {
    if (value == null || value === '') return 'Sin valor';

    // Formatear valores booleanos
    if (typeof value === 'boolean') {
      return value ? 'Sí' : 'No';
    }

    // Formatear números (salario, horas, etc.)
    if ((key === 'salario' || key === 'horas_semanales' || key === 'duracion_prueba_dias') &&
        (typeof value === 'number' || !isNaN(value))) {
      return new Intl.NumberFormat('es-DO').format(Number(value));
    }

    // Formatear fechas
    if (key.includes('fecha') && typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
      const sv = value.split('T')[0];
      return new Date(sv + 'T12:00:00').toLocaleDateString('es-DO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }

    // Para objetos anidados
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    return value.toString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-DO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return '-';
    const s = String(dateString).split('T')[0];
    const date = new Date(s + 'T12:00:00');
    return date.toLocaleDateString('es-DO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const hasAttachments = event.documentos_adjuntos &&
                        Array.isArray(event.documentos_adjuntos) &&
                        event.documentos_adjuntos.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-blue-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4 flex-1">
              <div className="bg-white/20 p-3 rounded-xl">
                <EventCategoryIcon categoria={event.categoria} size={28} showBackground={false} />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2">{event.titulo}</h2>
                <div className="flex flex-wrap items-center gap-2">
                  <EventTypeBadge tipo_evento={event.tipo_evento} categoria={event.categoria} />
                  <span className="px-2 py-1 bg-white/20 rounded text-sm">
                    {getCategoryLabel(event.categoria)}
                  </span>
                  {!event.activo && (
                    <span className="px-2 py-1 bg-red-500 rounded text-sm font-semibold">
                      Inactivo
                    </span>
                  )}
                </div>
              </div>
            </div>

            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors ml-4"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Descripción */}
          {event.descripcion && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <FileText size={16} />
                Descripción
              </h3>
              <p className="text-gray-800 leading-relaxed">{event.descripcion}</p>
            </div>
          )}

          {/* Fechas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <p className="text-sm text-blue-700 mb-1 flex items-center gap-2">
                <Calendar size={14} />
                Fecha Efectiva
              </p>
              <p className="text-lg font-semibold text-blue-900">
                {formatDateOnly(event.fecha_efectiva)}
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-sm text-gray-700 mb-1 flex items-center gap-2">
                <Clock size={14} />
                Fecha de Registro
              </p>
              <p className="text-lg font-semibold text-gray-900">
                {formatDate(event.created_at)}
              </p>
            </div>
          </div>

          {/* Valores (cambios) */}
          {(event.valor_anterior || event.valor_nuevo) && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Cambios Registrados
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {event.valor_anterior && (
                  <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                    <p className="text-sm text-red-700 font-semibold mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                      Valor Anterior
                    </p>
                    <div className="bg-white p-3 rounded border border-red-200 space-y-2">
                      {Object.entries(event.valor_anterior)
                        .filter(([key, value]) => !key.includes('_id'))
                        .map(([key, value]) => (
                          <div key={key} className="flex flex-col">
                            <span className="text-xs text-gray-500 uppercase tracking-wide">
                              {key.replace(/_/g, ' ')}
                            </span>
                            <span className="text-sm text-gray-900 font-medium mt-1">
                              {formatValue(key, value)}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                {event.valor_nuevo && (
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <p className="text-sm text-green-700 font-semibold mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      Valor Nuevo
                    </p>
                    <div className="bg-white p-3 rounded border border-green-200 space-y-2">
                      {Object.entries(event.valor_nuevo)
                        .filter(([key, value]) => !key.includes('_id'))
                        .map(([key, value]) => (
                          <div key={key} className="flex flex-col">
                            <span className="text-xs text-gray-500 uppercase tracking-wide">
                              {key.replace(/_/g, ' ')}
                            </span>
                            <span className="text-sm text-gray-900 font-medium mt-1">
                              {formatValue(key, value)}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Metadata */}
          {event.metadata && Object.keys(event.metadata).filter(k => !k.endsWith('_id')).length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Información Adicional
              </h3>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(event.metadata)
                    .filter(([key]) => !key.endsWith('_id'))
                    .map(([key, value]) => (
                    <div key={key} className="flex flex-col">
                      <span className="text-xs text-gray-600 mb-1 capitalize">
                        {key.replace(/_/g, ' ')}
                      </span>
                      <span className="text-sm text-gray-900 font-medium">
                        {typeof value === 'boolean'
                          ? (value ? 'Sí' : 'No')
                          : value?.toString() || '-'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Documento generado (desde solicitud aprobada) */}
          {event.document_id && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <FileText size={16} className="text-blue-600" />
                Documento Generado
              </h3>
              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-blue-900">{event.titulo}</p>
                  <p className="text-xs text-blue-600 mt-0.5">Generado automáticamente al aprobar</p>
                </div>
                <button
                  onClick={() => downloadDoc(event.document_id)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  <Download size={14} /> Descargar PDF
                </button>
              </div>
            </div>
          )}

          {/* Documentos adjuntos */}
          {hasAttachments && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Paperclip size={16} />
                Documentos Adjuntos ({event.documentos_adjuntos.length})
              </h3>
              <div className="space-y-2">
                {event.documentos_adjuntos.map((doc, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-blue-200 transition-colors"
                  >
                    {typeof doc === 'string' ? (
                      <a
                        href={doc}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm"
                      >
                        {doc}
                      </a>
                    ) : (
                      <div className="text-sm text-gray-800">
                        {doc.nombre || doc.url || 'Documento sin nombre'}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Auditoría */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Información de Auditoría
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600 mb-1">Creado por:</p>
                <p className="text-gray-900 font-medium flex items-center gap-2">
                  <User size={14} />
                  {event.creado_por || 'N/A'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatDate(event.created_at)}
                </p>
              </div>

              {event.aprobado_por && (
                <div>
                  <p className="text-gray-600 mb-1">Aprobado por:</p>
                  <p className="text-green-700 font-semibold flex items-center gap-2">
                    <User size={14} />
                    {event.aprobado_por}
                  </p>
                </div>
              )}

              {event.actualizado_por && (
                <div>
                  <p className="text-gray-600 mb-1">Actualizado por:</p>
                  <p className="text-gray-900 font-medium flex items-center gap-2">
                    <User size={14} />
                    {event.actualizado_por}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDate(event.updated_at)}
                  </p>
                </div>
              )}
            </div>

            {!event.activo && event.motivo_inactivacion && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 font-semibold mb-1">
                  Motivo de Inactivación:
                </p>
                <p className="text-sm text-red-900">{event.motivo_inactivacion}</p>
              </div>
            )}
          </div>

          {/* Botones */}
          <div className="flex items-center justify-center pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
